/**
 * Fachliche Auflösung rund um Projektzugriff – die Komponenten bekommen fertige Werte.
 *
 * Hier liegt die Zuordnung „Seite -> Bereich" und die Logik des Häkchen-Baums (voll /
 * teilweise / leer). Die Bereichsschlüssel selbst kommen vom Backend, nicht von hier.
 */
import { colors } from "../styles/colors";
import { PageType } from "../types";
import {
  BereichKey,
  BereichOption,
  FreigabeBereich,
  MeineRechte,
  Modus,
} from "../types/zugriff";
import { StatusVisual } from "./colors/statusVisual";

/** Welcher Bereich hinter welcher Projektseite steht. */
export const SEITE_ZU_BEREICH: Record<string, BereichKey> = {
  "project-overview": "uebersicht",
  "project-company": "gesellschaft",
  "project-area": "flaeche.flaechensicherung",
  "project-generation-units": "flaeche.anlagen",
  "project-yield": "flaeche.ertraege",
  "project-permit-applications": "genehmigung.antraege",
  // Die ELiA-Sicht eines Antrags ist der Antrag selbst – derselbe Bereich wie die Liste.
  "project-permit-elia": "genehmigung.antraege",
  "project-permit-evidences": "genehmigung.nachweise",
  "project-permit-costs": "genehmigung.kosten",
  "project-revenues": "kalkulation.erloese",
  "project-costs": "kalkulation.kosten",
  "project-financing": "kalkulation.finanzierung",
  "project-calculation": "kalkulation.wirtschaftlichkeit",
};

export const bereichZurSeite = (seite: PageType): BereichKey | undefined =>
  SEITE_ZU_BEREICH[seite];

export const darfSehen = (rechte: MeineRechte, bereich?: BereichKey): boolean =>
  bereich !== undefined && rechte[bereich] !== undefined;

export const darfBearbeiten = (
  rechte: MeineRechte,
  bereich?: BereichKey,
): boolean => bereich !== undefined && rechte[bereich] === "schreiben";

/** Zustand eines Oberpunkts im Freigabe-Baum. */
export type Häkchenzustand = "leer" | "teilweise" | "voll";

export interface Oberpunkt {
  key: string;
  label: string;
  blaetter: BereichOption[];
}

/**
 * Gruppiert den Katalog zu Oberpunkten. Implizite Bereiche (die Übersicht) tauchen nicht auf –
 * sie sind kein Häkchen, sondern Grundzugang jeder Freigabe.
 */
export const alsOberpunkte = (katalog: BereichOption[]): Oberpunkt[] => {
  const gruppen = new Map<string, Oberpunkt>();
  katalog
    .filter((b) => !b.implizit)
    .forEach((b) => {
      const vorhanden = gruppen.get(b.oberpunkt);
      if (vorhanden) {
        vorhanden.blaetter.push(b);
      } else {
        gruppen.set(b.oberpunkt, {
          key: b.oberpunkt,
          label: b.oberpunkt_label,
          blaetter: [b],
        });
      }
    });
  return [...gruppen.values()];
};

export const zustand = (
  blaetter: BereichOption[],
  gewaehlt: Map<BereichKey, Modus>,
): Häkchenzustand => {
  const treffer = blaetter.filter((b) => gewaehlt.has(b.key)).length;
  if (treffer === 0) return "leer";
  return treffer === blaetter.length ? "voll" : "teilweise";
};

/** Schreibrechte werden nur unter den freigegebenen Blättern gezählt. */
export const schreibZustand = (
  blaetter: BereichOption[],
  gewaehlt: Map<BereichKey, Modus>,
): Häkchenzustand => {
  const freigegeben = blaetter.filter((b) => gewaehlt.has(b.key));
  if (freigegeben.length === 0) return "leer";
  const schreibend = freigegeben.filter(
    (b) => gewaehlt.get(b.key) === "schreiben",
  ).length;
  if (schreibend === 0) return "leer";
  return schreibend === freigegeben.length ? "voll" : "teilweise";
};

export const alsFreigabeliste = (
  gewaehlt: Map<BereichKey, Modus>,
): FreigabeBereich[] =>
  [...gewaehlt.entries()].map(([bereich, modus]) => ({ bereich, modus }));

export const alsAuswahl = (
  bereiche: FreigabeBereich[],
): Map<BereichKey, Modus> => new Map(bereiche.map((b) => [b.bereich, b.modus]));

/**
 * Kurzfassung für die Liste: je Oberpunkt ein Chip, mit Hinweis auf Teilumfang und
 * Schreibrechte. Erwartet den Katalog, damit „teilweise" nicht geraten werden muss.
 */
export interface OberpunktChip {
  label: string;
  teilweise: boolean;
  schreibend: boolean;
}

export const chipsFuer = (
  bereiche: FreigabeBereich[],
  katalog: BereichOption[],
): OberpunktChip[] => {
  const gewaehlt = alsAuswahl(bereiche);
  return alsOberpunkte(katalog)
    .map((gruppe) => ({
      label: gruppe.label,
      teilweise: zustand(gruppe.blaetter, gewaehlt) === "teilweise",
      schreibend: schreibZustand(gruppe.blaetter, gewaehlt) !== "leer",
      leer: zustand(gruppe.blaetter, gewaehlt) === "leer",
    }))
    .filter((chip) => !chip.leer)
    .map(({ label, teilweise, schreibend }) => ({
      label,
      teilweise,
      schreibend,
    }));
};

/** Deckkraft des Chip-Hintergrunds als Hex-Suffix (~9 %) – wie bei den übrigen Status-Chips. */
const CHIP_HINTERGRUND_ALPHA = "18";

/**
 * Label und Farben des Freigabe-Status.
 *
 * „Eingeladen" bleibt stehen, bis die Person das Projekt zum ersten Mal öffnet; danach ist die
 * Freigabe an ihrem Benutzerkonto verankert und gilt als aktiv.
 */
export const freigabeStatusVisual = (istEingeladen: boolean): StatusVisual => {
  const color = istEingeladen
    ? colors.status.warning.main
    : colors.status.success.main;
  return {
    label: istEingeladen ? "Eingeladen" : "Aktiv",
    color,
    background: `${color}${CHIP_HINTERGRUND_ALPHA}`,
  };
};
